/**
 * Offline State Hooks
 * 
 * Custom hooks for accessing offline state, triggering sync operations,
 * and managing data loading in the offline-first architecture.
 */

import { useCallback, useEffect, useState } from 'react';
import { useOfflineState } from '../useOfflineState';
import { indexedDBService } from '@/services/offline';

/**
 * Hook for network status
 */
export function useNetworkStatus() {
  const { state } = useOfflineState();
  
  return {
    isOnline: state.isOnline,
    isConnected: state.isConnected,
    connectionQuality: state.connectionQuality,
    showOfflineIndicator: state.showOfflineIndicator
  };
}

/**
 * Hook for sync status and operations
 */
export function useSyncStatus() {
  const { state, setSyncError, setLoadingState, setSyncing, setSyncProgress } = useOfflineState();

  const performSync = useCallback(async () => {
    if (!state.isOnline) {
      setSyncError('Cannot sync while offline');
      return false;
    }

    // Prevent multiple simultaneous syncs
    if (state.isSyncing) {

      return false;
    }

    try {
      setLoadingState({ isLoadingData: true });

      // Import sync service dynamically to avoid circular dependencies
      const { syncService } = await import('@/services/offline');

      // Set up a polling mechanism to check sync status as a fallback
      const pollSyncStatus = () => {
        const status = syncService.getStatus();
        if (!status.isSyncing && state.isSyncing) {
          setSyncing(false);
        }
      };

      // Poll every 500ms during sync
      const pollInterval = setInterval(pollSyncStatus, 500);

      try {
        await syncService.forceSync();
      } finally {
        clearInterval(pollInterval);
        // Final check to ensure state is updated
        const finalStatus = syncService.getStatus();
        if (!finalStatus.isSyncing) {
          setSyncing(false);
        }
      }

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';
      console.error('❌ Manual sync failed:', errorMessage);
      setSyncError(errorMessage);
      return false;
    } finally {
      setLoadingState({ isLoadingData: false });
    }
  }, [state.isOnline, state.isSyncing, setSyncError, setLoadingState, setSyncing]);
  
  const performInitialLoad = useCallback(async () => {
    if (!state.isOnline) {
      setSyncError('Cannot perform initial load while offline');
      return false;
    }

    try {
      setLoadingState({ isLoadingData: true });

      // Import sync service dynamically to avoid circular dependencies
      const { syncService } = await import('@/services/offline');
      await syncService.performInitialLoad();


      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Initial load failed';
      console.error('❌ Initial load failed:', errorMessage);
      setSyncError(errorMessage);
      return false;
    } finally {
      setLoadingState({ isLoadingData: false });
    }
  }, [state.isOnline, setSyncError, setLoadingState]);
  
  const clearSyncError = useCallback(() => {
    setSyncError(null);
  }, [setSyncError]);

  const resetSyncState = useCallback(() => {
    setSyncing(false);
    setSyncProgress(null);
    setSyncError(null);

  }, [setSyncing, setSyncProgress, setSyncError]);

  return {
    isSyncing: state.isSyncing,
    lastSyncTime: state.lastSyncTime,
    syncError: state.syncError,
    syncProgress: state.syncProgress,
    showSyncProgress: state.showSyncProgress,
    pendingOperations: state.pendingOperations,
    failedOperations: state.failedOperations,
    conflictCount: state.conflictCount,
    performSync,
    performInitialLoad,
    clearSyncError,
    resetSyncState
  };
}

/**
 * Hook for data availability status
 */
export function useDataAvailability() {
  const { state, setLocalDataStatus } = useOfflineState();
  const [isChecking, setIsChecking] = useState(false);

  const checkDataAvailability = useCallback(async () => {
    // Prevent multiple simultaneous checks
    if (isChecking) {

      return { hasData: state.hasLocalData, isStale: state.isDataStale, lastUpdated: state.dataLastUpdated };
    }

    setIsChecking(true);
    try {
      // Ensure IndexedDB is initialized before accessing it
      await indexedDBService.initialize();

      const stats = await indexedDBService.getStats();
      const hasData = stats.trainingSessions > 0 || stats.techniques > 0;

      // Check if data is stale (older than 24 hours)
      const metadata = await indexedDBService.syncMetadata.getAllMetadata();
      let isStale = false;
      let lastUpdated: string | null = null;

      if (metadata.length > 0) {
        const latestSync = metadata.reduce((latest, meta) =>
          meta.last_sync > latest ? meta.last_sync : latest, '');
        lastUpdated = latestSync;

        const lastSyncTime = new Date(latestSync);
        const now = new Date();
        const hoursDiff = (now.getTime() - lastSyncTime.getTime()) / (1000 * 60 * 60);
        isStale = hoursDiff > 24;
      }

      setLocalDataStatus(hasData, isStale, lastUpdated);

      return { hasData, isStale, lastUpdated };
    } catch (error) {
      console.error('Failed to check data availability:', error);
      return { hasData: false, isStale: true, lastUpdated: null };
    } finally {
      setIsChecking(false);
    }
  }, [setLocalDataStatus, isChecking, state.hasLocalData, state.isDataStale, state.dataLastUpdated]);

  // Check data availability once offline state is initialized
  useEffect(() => {
    let mounted = true;

    const checkOnMount = async () => {
      // Wait for offline state to be initialized
      if (state.isInitializing) {
        return;
      }

      if (mounted && !isChecking) {
        await checkDataAvailability();
      }
    };

    checkOnMount();

    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isInitializing]); // Run when initialization state changes

  return {
    hasLocalData: state.hasLocalData,
    isDataStale: state.isDataStale,
    dataLastUpdated: state.dataLastUpdated,
    checkDataAvailability
  };
}

/**
 * Hook for loading states
 */
export function useLoadingState() {
  const { state, setLoadingState } = useOfflineState();
  
  const setInitializing = useCallback((isInitializing: boolean) => {
    setLoadingState({ isInitializing });
  }, [setLoadingState]);
  
  const setLoadingData = useCallback((isLoadingData: boolean) => {
    setLoadingState({ isLoadingData });
  }, [setLoadingState]);
  
  return {
    isInitializing: state.isInitializing,
    isLoadingData: state.isLoadingData,
    isLoading: state.isInitializing || state.isLoadingData || state.isSyncing,
    setInitializing,
    setLoadingData
  };
}

/**
 * Hook for error states
 */
export function useErrorState() {
  const { state, setErrorState } = useOfflineState();
  
  const setInitializationError = useCallback((error: string | null) => {
    setErrorState({ initializationError: error });
  }, [setErrorState]);
  
  const setDataError = useCallback((error: string | null) => {
    setErrorState({ dataError: error });
  }, [setErrorState]);
  
  const clearErrors = useCallback(() => {
    setErrorState({ initializationError: null, dataError: null });
  }, [setErrorState]);
  
  const hasErrors = state.initializationError !== null || 
                   state.dataError !== null || 
                   state.syncError !== null;
  
  return {
    initializationError: state.initializationError,
    dataError: state.dataError,
    syncError: state.syncError,
    hasErrors,
    setInitializationError,
    setDataError,
    clearErrors
  };
}

/**
 * Hook for sync statistics
 */
export function useSyncStats() {
  const [stats, setStats] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const refreshStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Import sync service dynamically to avoid circular dependencies
      const { syncService } = await import('@/services/offline');
      const syncStats = await syncService.getSyncStats();
      setStats(syncStats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get sync stats';
      setError(errorMessage);
      setStats({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh sync stats every 2 minutes (longer interval to prevent loops)
  useEffect(() => {
    // Initial load
    refreshStats();

    // Set up interval for periodic refresh
    const interval = setInterval(refreshStats, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [refreshStats]);
  
  return {
    stats,
    loading,
    error,
    refreshStats
  };
}

/**
 * Hook for offline operations
 */
export function useOfflineOperations() {
  const { state } = useOfflineState();
  const { performSync, performInitialLoad } = useSyncStatus();
  const { checkDataAvailability } = useDataAvailability();
  
  const canPerformOnlineOperations = state.isOnline && !state.isSyncing;
  const shouldShowOfflineMessage = !state.isOnline || !state.hasLocalData;
  
  const initializeOfflineMode = useCallback(async () => {
    try {
      // Check if we have local data (but don't await to prevent loops)
      checkDataAvailability().catch(err =>
        console.warn('Data availability check failed during initialization:', err)
      );

      // If online and no local data, perform initial load
      if (state.isOnline && !state.hasLocalData) {
        await performInitialLoad();
      }

      return true;
    } catch (error) {
      console.error('Failed to initialize offline mode:', error);
      return false;
    }
  }, [state.isOnline, state.hasLocalData, checkDataAvailability, performInitialLoad]);
  
  const syncWhenOnline = useCallback(async () => {
    if (state.isOnline && !state.isSyncing) {
      return await performSync();
    }
    return false;
  }, [state.isOnline, state.isSyncing, performSync]);
  
  return {
    canPerformOnlineOperations,
    shouldShowOfflineMessage,
    initializeOfflineMode,
    syncWhenOnline
  };
}

/**
 * Hook for connection quality monitoring
 * Note: This hook now only provides access to connection quality state.
 * The actual monitoring is handled by the OfflineStateProvider to prevent
 * multiple instances from running simultaneously.
 */
export function useConnectionQuality() {
  const { state } = useOfflineState();

  return {
    connectionQuality: state.connectionQuality,
    isMonitoring: false, // Monitoring is handled at provider level
    startMonitoring: () => {}, // No-op, monitoring is automatic
    stopMonitoring: () => {} // No-op, monitoring is automatic
  };
}
